import { SkyButton } from "@skypointcloud/skypoint-ui";
import { ExternalLink, RefreshCw } from "lucide-react";
import { ThemedLogo } from "./theme/ThemedLogo";

interface IframeAuthErrorProps {
  errorMessage?: string;
  onRetry?: () => void;
}

export const IframeAuthError = ({ errorMessage, onRetry }: IframeAuthErrorProps) => {
  const openInNewWindow = () => {
    window.open(window.location.href, "_blank", "noopener,noreferrer");
  };

  const isInIframe = window.self !== window.top;

  return (
    <div
      className="dark:bg-background!"
      style={styles.container}
    >
      <div
        className="dark:bg-card!"
        style={styles.card}
      >
        <div style={styles.logoSection}>
          <ThemedLogo
            src="/dist/img/skyHire_Dark.svg"
            darkSrc="/dist/img/skyHire_Light.svg"
            alt="skyHire"
            style={styles.logo}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </div>

        <div style={styles.iconContainer}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="#0078d4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 8V12"
              stroke="#0078d4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 16H12.01"
              stroke="#0078d4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h2
          className="dark:text-foreground!"
          style={styles.title}
        >
          Sign-in Requires a New Window
        </h2>

        <p
          className="dark:text-muted-foreground!"
          style={styles.description}
        >
          {isInIframe
            ? "For security reasons, authentication cannot be completed within this embedded view. Please open skyHire in a new browser window to sign in."
            : errorMessage ||
              "There was an issue with authentication. Please try again or open in a new window."}
        </p>

        <div style={styles.buttonGroup}>
          <SkyButton
            appearance="primary"
            size="large"
            icon={ExternalLink}
            onClick={openInNewWindow}
            className="w-full h-[44px] text-[15px] font-semibold"
          >
            Open in New Window
          </SkyButton>

          <SkyButton
            appearance="secondary"
            size="large"
            icon={RefreshCw}
            onClick={() => window.location.reload()}
            className="w-full h-[44px] text-[15px]"
          >
            Try Again
          </SkyButton>
        </div>

        <p
          className="dark:text-muted-foreground!"
          style={styles.helpText}
        >
          Having trouble?{" "}
          <a
            href="https://skypoint.ai/support"
            target="_blank"
            rel="noopener noreferrer"
            className="dark:text-[#62abf5]!"
            style={styles.link}
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    padding: "20px",
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Oxygen', 'Ubuntu', sans-serif",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
    padding: "48px 40px",
    maxWidth: "440px",
    width: "100%",
    textAlign: "center" as const,
  },
  logoSection: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "center",
  },
  logo: {
    height: "48px",
  },
  iconContainer: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "center",
  },
  title: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#323130",
    margin: "0 0 16px 0",
  },
  description: {
    fontSize: "14px",
    color: "#605e5c",
    lineHeight: 1.6,
    margin: "0 0 32px 0",
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    marginBottom: "24px",
  },
  primaryButton: {
    width: "100%",
    height: "44px",
    fontSize: "15px",
    fontWeight: 600,
  },
  secondaryButton: {
    width: "100%",
    height: "44px",
    fontSize: "15px",
  },
  helpText: {
    fontSize: "13px",
    color: "#8a8886",
    margin: 0,
  },
  link: {
    color: "#0078d4",
    textDecoration: "none",
  },
};

export default IframeAuthError;
